import { useIsFetching, useIsMutating } from '@tanstack/react-query'
import { useRouterState } from '@tanstack/react-router'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useState } from 'react'

/**
 * YouTube-style top progress bar.
 *
 * Active when:
 * - TanStack Query has >0 queries or mutations in flight
 * - TanStack Router is transitioning to a new route
 *
 * Fades in after a short grace period so instant responses don't flash the bar.
 */
export function TopProgressBar() {
  const fetching = useIsFetching()
  const mutating = useIsMutating()
  const routerBusy = useRouterState({
    select: (s) => s.status === 'pending' || s.isLoading,
  })

  const busy = fetching > 0 || mutating > 0 || routerBusy
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    if (!busy) {
      setVisible(false)
      return
    }
    const timer = window.setTimeout(() => setVisible(true), 120)
    return () => window.clearTimeout(timer)
  }, [busy])

  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          key="progress"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          // biome-ignore lint/a11y/useSemanticElements: motion.div needs role="status" — <output> cannot be animated as an overlay
          className="pointer-events-none fixed inset-x-0 top-0 z-[60] h-0.5 overflow-hidden"
          role="status"
          aria-label="Loading"
        >
          <motion.div
            className="h-full bg-gradient-to-r from-muted-foreground via-foreground to-muted-foreground"
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 1.2, ease: 'easeInOut', repeat: Number.POSITIVE_INFINITY }}
            style={{ width: '40%' }}
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}
